import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Trash2, Copy, Check, Upload } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useBusinessId } from '../../hooks/useBusinessId';
import { toast } from 'sonner';
import SelectServiceModal from '../../components/manage/SelectServiceModal';
import { Service, ServiceCategory } from '../../lib/services';
import { useBusiness } from '../../contexts/BusinessContext';
import { Switch } from '@headlessui/react';

interface SubCategory {
  id: string;
  title: string;
  services: Array<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  }>;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  category_title: string;
  sub_categories?: SubCategory[];
}

export default function OnlineBookings() {
  const supabase = useSupabase();
  const businessId = useBusinessId();
  const { selectedBusiness, updateBusiness } = useBusiness();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
  const [subCategoryTitle, setSubCategoryTitle] = useState("");
  const [isAddingToSubCategory, setIsAddingToSubCategory] = useState(false);
  const [isAssignServicesModalOpen, setIsAssignServicesModalOpen] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  
  // New state variables for color picker
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [navbarColor, setNavbarColor] = useState('#000000'); // Default black color
  
  // New state variables for logo upload
  const [isUploading, setIsUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Buffer time state
  const [bufferTime, setBufferTime] = useState<number>(30); // Default to 30 minutes

  // New state variables for service info modal
  const [isServiceInfoModalOpen, setIsServiceInfoModalOpen] = useState(false);
  const [selectedServiceInfo, setSelectedServiceInfo] = useState<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  } | null>(null);

  const handleToggleOnlineBookings = async (enabled: boolean) => {
    if (!selectedBusiness) return;

    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        allow_online_bookings: enabled
      };

      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });

      toast.success(`Online bookings ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating online bookings status:', error);
      toast.error('Failed to update online bookings status');
    }
  };

  // Fetch existing categories on component mount
  useEffect(() => {
    if (businessId) {
      fetchCategories();
    }
  }, [businessId]);

  // Load navbar color from business settings
  useEffect(() => {
    if (selectedBusiness?.booking_settings?.navbar_color) {
      setNavbarColor(selectedBusiness.booking_settings.navbar_color);
    }
  }, [selectedBusiness]);

  // Load logo URL from business settings
  useEffect(() => {
    if (selectedBusiness?.booking_settings?.logo_url) {
      setLogoUrl(selectedBusiness.booking_settings.logo_url);
    }
  }, [selectedBusiness]);

  // Load buffer time from online_booking_settings
  useEffect(() => {
    if (businessId) {
      fetchBufferTime();
    }
  }, [businessId]);

  const fetchBufferTime = async () => {
    if (!supabase || !businessId) return;
    
    console.log(`[BUFFER TIME] Fetching buffer time for business ${businessId}`);
    
    try {
      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('buffer_time')
        .eq('business_id', businessId)
        .single();
      
      console.log('[BUFFER TIME] Fetch result:', data, error);
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found, will use default value
          console.log('[BUFFER TIME] No buffer time setting found, using default');
        } else {
          console.error('[BUFFER TIME] Error fetching buffer time:', error);
        }
        return;
      }
      
      if (data?.buffer_time !== undefined && data?.buffer_time !== null) {
        console.log(`[BUFFER TIME] Setting buffer time to ${data.buffer_time} minutes from database`);
        setBufferTime(data.buffer_time);
      } else {
        console.log('[BUFFER TIME] Buffer time value in database is undefined or null, using default');
      }
    } catch (error) {
      console.error('[BUFFER TIME] Error fetching buffer time:', error);
    }
  };

  const handleBufferTimeChange = async (minutes: number) => {
    if (!supabase || !businessId) return;
    
    try {
      // Update UI immediately
      setBufferTime(minutes);
      console.log(`[BUFFER TIME] Setting buffer time to ${minutes} minutes for business ${businessId}`);
      
      // Use upsert pattern (on conflict do update) for more reliable operation
      const { data, error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { 
            business_id: businessId, 
            buffer_time: minutes 
          },
          { 
            onConflict: 'business_id'
          }
        )
        .select();
      
      console.log('[BUFFER TIME] Upsert result:', data, error);
      
      if (error) {
        throw error;
      }
      
      toast.success(`Buffer time set to ${minutes} minutes`);
    } catch (error) {
      console.error('[BUFFER TIME] Error saving buffer time:', error);
      toast.error('Failed to save buffer time. Please try again.');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('online_booking_flow')
        .select('id, category_title, sub_categories')
        .eq('business_id', businessId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryTitle.trim() || !businessId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('online_booking_flow')
        .insert([{
          business_id: businessId,
          category_title: categoryTitle.trim()
        }])
        .select()
        .single();

      if (error) throw error;

      setCategories([...categories, data]);
      setIsModalOpen(false);
      setCategoryTitle('');
      toast.success('Category created successfully');
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to create category');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlusClick = (categoryId: string, isSubCategoryPlus: boolean = false) => {
    setSelectedCategoryId(categoryId);
    setIsAddingToSubCategory(isSubCategoryPlus);
    setIsOptionsModalOpen(true);
  };

  const handleAddSubCategory = async () => {
    if (!subCategoryTitle.trim() || !selectedCategoryId) {
      toast.error("Please enter a title for the sub-category");
      return;
    }

    try {
      // Get the current category to access its existing sub-categories
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      // Create new sub-category object
      const newSubCategory = {
        id: crypto.randomUUID(),
        title: subCategoryTitle.trim(),
        services: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Combine existing sub-categories with new one
      const updatedSubCategories = [
        ...(currentCategory?.sub_categories || []),
        newSubCategory
      ];

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Refresh categories to get latest data
      await fetchCategories();

      setSubCategoryTitle("");
      setIsSubCategoryModalOpen(false);
      toast.success("Sub-category added successfully");
    } catch (error) {
      console.error('Error adding sub-category:', error);
      toast.error('Failed to add sub-category');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('online_booking_flow')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      // Refresh categories to get latest data
      await fetchCategories();
      
      toast.success("Category deleted successfully");
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleDeleteSubCategory = async (subCategory: SubCategory) => {
    try {
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      const updatedSubCategories = currentCategory?.sub_categories?.filter((sc: SubCategory) => sc.id !== subCategory.id) || [];

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Refresh categories to get latest data
      await fetchCategories();
      
      toast.success("Sub-category deleted successfully");
    } catch (error) {
      console.error('Error deleting sub-category:', error);
      toast.error('Failed to delete sub-category');
    }
  };

  const handleServiceAdd = async (service: Service, priceOption: any) => {
    if (!selectedSubCategory || !selectedCategoryId || !businessId) return;

    try {
      // Get the current category to access its existing sub-categories
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      // Check if service is already assigned
      const isServiceAssigned = currentCategory?.sub_categories?.some((subCat: SubCategory) => 
        subCat.id === selectedSubCategory.id && 
        subCat.services?.some((s: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => 
          s.service.id === service.id && s.priceOption.id === priceOption.id
        )
      );

      if (isServiceAssigned) {
        toast.error('This service is already assigned to this sub-category');
        return;
      }

      // Update the sub-categories array with the new service
      const updatedSubCategories = currentCategory?.sub_categories?.map((subCat: SubCategory) => {
        if (subCat.id === selectedSubCategory.id) {
          return {
            ...subCat,
            services: [
              ...(subCat.services || []),
              {
                service: service,
                priceOption: priceOption
              }
            ]
          };
        }
        return subCat;
      });

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Update local UI state
      const updatedCategories = categories.map(category => {
        if (category.id === selectedCategoryId) {
          return {
            ...category,
            sub_categories: updatedSubCategories
          };
        }
        return category;
      });

      setCategories(updatedCategories);
      toast.success('Service assigned successfully');
    } catch (error) {
      console.error('Error assigning service:', error);
      toast.error('Failed to assign service');
    }
  };

  const handleDeleteService = async (subCategory: SubCategory, service: Service) => {
    try {
      // Find the category that contains this sub-category
      const category = categories.find(cat => 
        cat.sub_categories?.some(sc => sc.id === subCategory.id)
      );

      if (!category) {
        throw new Error('Category not found');
      }

      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', category.id)
        .single();

      if (fetchError) throw fetchError;

      const updatedSubCategories = currentCategory?.sub_categories?.map((sc: SubCategory) => {
        if (sc.id === subCategory.id) {
          return {
            ...sc,
            services: (sc.services || []).filter((s: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => s.service.id !== service.id)
          };
        }
        return sc;
      });

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', category.id);

      if (updateError) throw updateError;

      // Update local UI state
      const updatedCategories = categories.map(cat => {
        if (cat.id === category.id) {
          return {
            ...cat,
            sub_categories: updatedSubCategories
          };
        }
        return cat;
      });

      setCategories(updatedCategories);
      toast.success('Service removed successfully');
    } catch (error) {
      console.error('Error removing service:', error);
      toast.error('Failed to remove service');
    }
  };

  const handleSubCategoryDrop = (subCat: SubCategory) => {
    // ... existing code ...
  };

  const handleServiceDrop = (service: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => {
    // ... existing code ...
  };

  const handleSubCategoryDragStart = (subCat: SubCategory) => {
    // ... existing code ...
  };

  const handleCopyUrl = async () => {
    if (!selectedBusiness?.slug) return;
    
    const url = `${window.location.origin}/${selectedBusiness.slug}/book`;
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      toast.error('Failed to copy URL');
    }
  };

  const handleSaveNavbarColor = async () => {
    if (!selectedBusiness) return;
    
    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        navbar_color: navbarColor
      };
      
      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });
      
      setIsColorPickerOpen(false);
      toast.success('Navigation bar color updated');
    } catch (error) {
      console.error('Error updating navbar color:', error);
      toast.error('Failed to update navigation bar color');
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0 || !selectedBusiness) {
      return;
    }
    
    const file = event.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${selectedBusiness.id}-logo.${fileExt}`;
    const filePath = `business-logos/${fileName}`;
    
    console.log('Uploading logo with path:', filePath);
    setIsUploading(true);
    
    try {
      // Check if the bucket exists
      const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
      console.log('Available buckets:', buckets);
      
      if (bucketsError) {
        console.error('Error listing buckets:', bucketsError);
        throw bucketsError;
      }
      
      // Use the "avatars" bucket which should already exist
      const bucketName = 'avatars'; // Changed from 'public' to 'avatars'
      
      // Upload file to storage
      console.log(`Uploading to bucket "${bucketName}" with path "${filePath}"`);
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, { upsert: true });
        
      console.log('Upload response:', { uploadData, uploadError });
      if (uploadError) throw uploadError;
      
      // Get public URL
      console.log(`Getting public URL for "${filePath}"`);
      const { data } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);
      
      console.log('Public URL data:', data);
      if (!data.publicUrl) throw new Error('Failed to get public URL');
      
      // Update business settings with logo URL
      console.log('Updating business settings with logo URL:', data.publicUrl);
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        logo_url: data.publicUrl
      };
      
      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });
      
      setLogoUrl(data.publicUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Add new function to delete logo
  const handleDeleteLogo = async () => {
    if (!selectedBusiness || !logoUrl) return;
    
    try {
      // Extract the file path from the URL
      const urlParts = logoUrl.split('/');
      const filePath = urlParts[urlParts.length - 2] + '/' + urlParts[urlParts.length - 1];
      
      // Delete file from storage
      const bucketName = 'avatars';
      const { error: deleteError } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);
        
      if (deleteError) {
        console.warn('Error deleting logo from storage:', deleteError);
        // Continue anyway to remove from business settings
      }
      
      // Update business settings to remove logo URL
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        logo_url: undefined  // Change null to undefined to match the BookingSettings type
      };
      
      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });
      
      setLogoUrl(null);
      toast.success('Logo removed successfully');
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Failed to delete logo');
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Online Bookings</h1>
        <p className="mt-1 text-sm text-gray-500">Configure your online booking flow and settings</p>
      </div>

      {/* Online Bookings Toggle */}
      {selectedBusiness && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 md:p-8">
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Online Booking Portal</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Allow customers to book services through your online portal
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <Switch
                  checked={selectedBusiness.booking_settings?.allow_online_bookings || false}
                  onChange={handleToggleOnlineBookings}
                  className={`${
                    selectedBusiness.booking_settings?.allow_online_bookings ? 'bg-gray-900' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      selectedBusiness.booking_settings?.allow_online_bookings ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
                <a
                  href={`/${selectedBusiness.slug}/book`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  Preview
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 md:p-8">
          <div className="flex flex-col space-y-8">
            <div className="w-full md:w-1/4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Create Your Online Booking Flow</h3>
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Category
              </button>
            </div>
            
            {/* Single Column Flow Layout for Both Mobile and Desktop */}
            <div className="flex justify-center w-full overflow-x-auto pb-4">
              <div className="flex flex-col items-center space-y-8 min-w-max pb-4">
                {categories.map(category => (
                  <div key={category.id} className="flex flex-col items-center">
                    <div className="relative flex items-center">
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="absolute -top-2 -right-2 p-1.5 bg-gray-900 rounded-full text-white hover:bg-gray-800 transition-colors z-20"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                      <span className="inline-flex items-center px-6 py-3 rounded-md text-sm font-medium bg-gray-100 text-gray-800 border border-black">
                        {category.category_title}
                      </span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-px h-3 bg-gray-300"></div>
                      {!category.sub_categories?.length && (
                        <button
                          onClick={() => handlePlusClick(category.id)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-900 text-white hover:bg-gray-800 my-1"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      )}
                      <div className="w-px h-3 bg-gray-300"></div>
                    </div>
                    {/* Sub-categories */}
                    {category.sub_categories && category.sub_categories.length > 0 && (
                      <div className="flex flex-col items-center">
                        {category.sub_categories.map((subCategory, index) => (
                          <div key={index} className="flex flex-col items-center">
                            <div className="relative flex items-center w-full">
                              <button
                                onClick={() => handleDeleteSubCategory(subCategory)}
                                className="absolute -top-2 -right-2 p-1.5 bg-gray-900 rounded-full text-white hover:bg-gray-800 transition-colors z-20"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                              <span className="w-40 min-w-[10rem] px-4 py-2 text-sm bg-blue-100 text-black rounded-md flex justify-center border border-black">
                                {subCategory.title}
                              </span>
                            </div>
                            <div className="flex flex-col items-center space-y-2 w-40 min-w-[10rem]">
                              <div className="w-px h-1 bg-gray-300"></div>
                              <button
                                onClick={() => {
                                  setSelectedCategoryId(category.id);
                                  setSelectedSubCategory(subCategory);
                                  setIsSelectServiceModalOpen(true);
                                }}
                                className="w-full px-3 py-2 text-xs md:py-0.5 font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                              >
                                Assign Services
                              </button>
                              {/* Display assigned services */}
                              {subCategory.services.length > 0 && (
                                <div className="flex flex-col items-center space-y-2 mt-1 w-full">
                                  {subCategory.services.map((service, serviceIndex) => (
                                    <div key={serviceIndex} className="relative w-full">
                                      <button
                                        onClick={() => handleDeleteService(subCategory, service.service)}
                                        className="absolute -top-2 -right-2 p-1.5 bg-gray-900 rounded-full text-white hover:bg-gray-800 transition-colors z-20"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                      <span className="text-sm text-black px-3 py-1 bg-yellow-100 rounded flex flex-col items-center w-full border border-black relative">
                                        <button 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedServiceInfo(service);
                                            setIsServiceInfoModalOpen(true);
                                          }}
                                          className="absolute top-1 left-1 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-blue-600 z-10"
                                          title="View service details"
                                        >
                                          i
                                        </button>
                                        <span>{service.service.name}</span>
                                        <span className="text-xs text-gray-500">{service.priceOption.name}</span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="w-px h-3 bg-gray-300"></div>
                            </div>
                            {index === category.sub_categories!.length - 1 && (
                              <>
                                <div className="w-px h-3 bg-gray-300"></div>
                                <button
                                  onClick={() => handlePlusClick(category.id, true)}
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-900 text-white hover:bg-gray-800 my-1"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                                <div className="w-px h-3 bg-gray-300"></div>
                              </>
                            )}
                            {index !== category.sub_categories!.length - 1 && (
                              <div className="w-px h-3 bg-gray-300"></div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Page Customization Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 md:p-8">
          <div className="flex flex-col space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-4">Customize Your Online Booking Page</h3>
              <p className="text-sm text-gray-500 mb-4">Personalize the appearance of your booking page to match your brand</p>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              {/* Logo Upload */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Business Logo</h4>
                <p className="text-xs text-gray-500 mb-4">Upload your logo to display on your booking page (recommended size: 200x60px)</p>
                
                {logoUrl && (
                  <div className="mb-4 p-2 border border-gray-200 rounded bg-white">
                    <img 
                      src={logoUrl} 
                      alt="Business logo" 
                      className="h-12 max-w-full object-contain"
                    />
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isUploading ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </button>
                  
                  {logoUrl && (
                    <button 
                      onClick={handleDeleteLogo}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Logo
                    </button>
                  )}
                </div>
              </div>
              
              {/* Navbar Color */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Navigation Bar Color</h4>
                <p className="text-xs text-gray-500 mb-4">Select a color for the top navigation bar on your booking page</p>
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-8 h-8 rounded border border-gray-300" 
                    style={{ backgroundColor: navbarColor }}
                  ></div>
                  <button 
                    onClick={() => setIsColorPickerOpen(true)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    Choose Color
                  </button>
                </div>
              </div>
            
              {/* Buffer Time */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Buffer Time</h4>
                <p className="text-xs text-gray-500 mb-4">Set a buffer time between mobile bookings to allow for travel time</p>
                
                <div className="relative mb-4">
                  <select
                    value={bufferTime}
                    onChange={(e) => handleBufferTimeChange(Number(e.target.value))}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm rounded-md"
                  >
                    <option value="0">No buffer time</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500">
                  Buffer time helps ensure technicians have enough time to travel between appointments.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Category Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="relative z-50"
      >
        {/* Background overlay */}
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        {/* Modal position */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Create Category
              </Dialog.Title>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={categoryTitle}
                  onChange={(e) => setCategoryTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm"
                  placeholder="Enter category title"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategory}
                  disabled={isLoading}
                  className="px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Options Modal */}
      <Dialog
        open={isOptionsModalOpen}
        onClose={() => setIsOptionsModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Add Sub-category
              </Dialog.Title>
              <button
                onClick={() => setIsOptionsModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setIsOptionsModalOpen(false);
                  setIsSubCategoryModalOpen(true);
                }}
                className="w-full px-4 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-md"
              >
                Create Sub Categories
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Sub-category Modal */}
      <Dialog
        open={isSubCategoryModalOpen}
        onClose={() => setIsSubCategoryModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Add Sub-category
              </Dialog.Title>
              <button
                onClick={() => setIsSubCategoryModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              <label htmlFor="subCategoryTitle" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                id="subCategoryTitle"
                value={subCategoryTitle}
                onChange={(e) => setSubCategoryTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter sub-category title"
              />
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsSubCategoryModalOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSubCategory}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Select Service Modal */}
      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => {
          setIsSelectServiceModalOpen(false);
          setSelectedSubCategory(null);
        }}
        onAdd={handleServiceAdd}
      />

      {/* Color Picker Modal */}
      <Dialog
        open={isColorPickerOpen}
        onClose={() => setIsColorPickerOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Choose Navigation Bar Color
              </Dialog.Title>
              <button
                onClick={() => setIsColorPickerOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              <div className="flex flex-col space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Select a color
                </label>
                <input
                  type="color"
                  value={navbarColor}
                  onChange={(e) => setNavbarColor(e.target.value)}
                  className="w-full h-12 p-1 rounded cursor-pointer"
                />
                
                <div className="flex items-center space-x-2 mt-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Color code
                    </label>
                    <input
                      type="text"
                      value={navbarColor}
                      onChange={(e) => {
                        // Validate hex color format
                        const isValidHex = /^#([0-9A-F]{3}){1,2}$/i.test(e.target.value);
                        if (isValidHex || e.target.value.startsWith('#')) {
                          setNavbarColor(e.target.value);
                        }
                      }}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="w-12 h-12 rounded border border-gray-300" style={{ backgroundColor: navbarColor }}></div>
                </div>
                
                <div className="mt-2">
                  <p className="text-sm text-gray-500">Preview:</p>
                  <div className="mt-2 w-full h-12 rounded-t-lg" style={{ backgroundColor: navbarColor }}></div>
                  <div className="bg-white border border-gray-300 border-t-0 h-12 rounded-b-lg flex items-center justify-center text-sm text-gray-700">
                    Booking Page Preview
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsColorPickerOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNavbarColor}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Color
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Service Info Modal */}
      <Dialog
        open={isServiceInfoModalOpen}
        onClose={() => setIsServiceInfoModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900 truncate max-w-xs">
                {selectedServiceInfo?.service.name || "Service Details"}
              </Dialog.Title>
              <button
                onClick={() => setIsServiceInfoModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedServiceInfo && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Service Name</h3>
                  <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.service.name}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Description</h3>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedServiceInfo.service.description || "No description available"}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Price Option</h3>
                    <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.priceOption.name}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Price</h3>
                    <p className="mt-1 text-sm text-gray-900">${selectedServiceInfo.priceOption.price.toFixed(2)}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Duration</h3>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedServiceInfo.priceOption.duration_minutes >= 60 
                        ? `${Math.floor(selectedServiceInfo.priceOption.duration_minutes / 60)} hour${selectedServiceInfo.priceOption.duration_minutes >= 120 ? 's' : ''} ${selectedServiceInfo.priceOption.duration_minutes % 60 > 0 ? `${selectedServiceInfo.priceOption.duration_minutes % 60} min` : ''}`
                        : `${selectedServiceInfo.priceOption.duration_minutes} min`
                      }
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Tax Exempt</h3>
                    <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.service.tax_exempt ? "Yes" : "No"}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsServiceInfoModalOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
} 